// Central description of the ribbon sidebar menu (new UI theme).
// One entry per ROOT (ribbon icon). A root is either a direct link (type:'link')
// or opens a panel of items. `gate` reproduces the exact role checks from the
// classic sidebar so visibility is identical — only how the menu is drawn changes.
//
// zone: 'bottom'  → pinned to the bottom of the ribbon, under a divider (admin tools).
// view    -> the currentView key App.js already renders (navigateTo target)
// href    -> external link (opens new tab)
// soon    -> disabled placeholder
// gate    -> see canSee() below

export const ROOTS = [
  { id: 'overview', label: 'Overview', icon: 'overview', gate: 'all', type: 'link', view: 'dashboard' },

  // Apps = first-party views that render INSIDE the content window (our own components).
  { id: 'apps', label: 'Apps', icon: 'apps', gate: 'all', kind: 'apps', items: [
    { label: 'Chat',  icon: 'chat',  view: 'chat', badge: 'chat' },
    { label: 'Drive', icon: 'drive', view: 'google-drive' },
    { label: 'Gmail', icon: 'gmail', view: 'gmail' },
  ] },

  // Links = catalog of external sites (see linksData.js). Master-detail: the panel
  // lists categories, the content pane shows that category's tiles; each opens a new tab.
  { id: 'links', label: 'Links', icon: 'links', gate: 'all', kind: 'links', view: 'links' },

  { id: 'workspace', label: 'My Workspace', icon: 'workspace', gate: 'all', items: [
    { label: 'Inventory', icon: 'inventory', view: 'my-hardware' },
    { label: 'Timesheet', icon: 'forms',     view: 'timesheet' },
    { label: 'Forms',     icon: 'forms',     view: 'forms' },
  ] },

  // Departments = the museum's operating departments. Human Resources is live (opens
  // the HR hub with its own tabs); the rest are placeholders for now.
  { id: 'departments', label: 'Departments', icon: 'departments', gate: 'all', items: [
    { label: 'Human Resources',  icon: 'hr',          view: 'human-resources' },
    { label: 'Exhibitions',      icon: 'departments', soon: true },
    { label: 'Facilities',       icon: 'facilities',  view: 'facilities', gate: 'facilities' },
    { label: 'Visitor Services', icon: 'departments', soon: true },
    { label: 'Operations',       icon: 'departments', view: 'operations', gate: 'operations' },
    { label: 'Accounting',       icon: 'departments', soon: true },
    { label: 'Development',      icon: 'departments', soon: true },
    { label: 'Communications',   icon: 'departments', soon: true },
  ] },

  // Security = surveillance / gallery traffic / incidents / access control. Opens
  // the Security hub (its own internal tabs). Gated to the elevated tier for now.
  { id: 'security', label: 'Security', icon: 'security', gate: 'security', type: 'link', view: 'security', pinAboveDivider: true },

  // ── Bottom admin cluster (under a divider) ─────────────────────────────────
  { id: 'settings', label: 'Settings', icon: 'settings', gate: 'admins', zone: 'bottom', items: [
    { label: 'Account Settings',     icon: 'settings', view: 'account-settings' },
    { label: 'Inventory Management', icon: 'invmgmt',  view: 'inventory',        gate: 'admin' },
    { label: 'Chat Transcripts',     icon: 'chat',     view: 'chat-transcripts', gate: 'admin' },
  ] },

  { id: 'reports', label: 'Reports', icon: 'clipboard', gate: 'owners', zone: 'bottom', items: [
    { label: 'Inventory Report', icon: 'clipboard', view: 'reports',         gate: 'admin' },
    { label: 'Time Off Report',  icon: 'cal',       view: 'time-off-report', gate: 'reports' },
    { label: 'KnowBe4 Report',   icon: 'shield',    view: 'knowbe4-report',  gate: 'admin' },
  ] },

  { id: 'integrations', label: 'Integrations', icon: 'integrations', gate: 'admins', zone: 'bottom', items: [
    { label: 'Google Workspace',    icon: 'drive', soon: true },
    { label: 'KnowBe4',             icon: 'shield', soon: true },
    { label: 'Broadcast Messenger', icon: 'chat',  view: 'broadcast-messenger' },
    { label: 'Asana',               icon: 'asana', soon: true },
  ] },

  { id: 'users', label: 'Users, Roles & Permissions', icon: 'people', gate: 'admins', zone: 'bottom', items: [
    { label: 'User Management',    icon: 'users',  view: 'user-management' },
    { label: 'Roles & Permissions', icon: 'shield', soon: true },
  ] },
];

// Mirrors the classic sidebar's inline guards exactly.
export function canSee(gate, user) {
  // `role` is normalized to 'admin' for all elevated tiers (owner/admin/supervisor)
  // in App.js normalizeUser; staff keep 'staff'. So for now these admin-tier gates
  // all resolve to "any elevated user". Staff-visible items use gate 'all'.
  const role = user?.role;
  switch (gate) {
    case 'admin':         return role === 'admin';
    // 'admins' = Owner/Admin only (excludes Supervisor) — checks the TRUE role.
    case 'admins':        return ['owner', 'admin'].includes(user?.roleLabel);
    case 'owners':        return role === 'admin';   // any elevated tier incl. Supervisor (Reports)
    case 'reports':       return role === 'admin';
    case 'facilities':    return role === 'admin';
    // Operations = explicit 4-person allowlist by username (incl. a staff-role user, Pat).
    case 'operations':    return ['memmetti', 'jlamirault', 'sfoley', 'ptaylor'].includes(user?.username);
    case 'security':      return role === 'admin';
    case 'hr-openings':   return role === 'admin';
    case 'hr-onboarding': return role === 'admin';
    case 'all':
    default:              return true;
  }
}

// Which root "owns" a given view (for active-state highlighting).
export function rootOfView(view) {
  return ROOTS.find(r => r.view === view || (r.items || []).some(i => i.view === view)) || null;
}
